import React, { FC, ReactNode } from 'react';
import ReactDropzone from 'react-dropzone';
import styled from 'styled-components';

import type { DropzoneFile } from '@rover/types/src/ImagePicker';

export type Props = {
  children?:
    | ReactNode
    | ((props: {
        isDragActive: boolean;
        isDragAccept: boolean;
        isDragReject: boolean;
        multiple: boolean;
        draggedFiles: any[];
      }) => ReactNode);
  disabled?: boolean;
  className?: string; // included to support an odd use in DropzoneDisplay.tsx that I didn't want to try and remove
  onDrop?: (acceptedFiles: DropzoneFile[], rejectedFiles: DropzoneFile[]) => void;
  onUpload?: (acceptedFiles: DropzoneFile[], rejectedFiles: DropzoneFile[]) => void;
  onDropAccepted?: (acceptedFiles: DropzoneFile[]) => void;
  inputProps?: Record<string, string>;
  multiple?: boolean;
  activeStyle?: React.ComponentProps<typeof ReactDropzone>['activeStyle'];
  acceptImageAndPDF?: boolean;
  role?: string;
  tabIndex?: number;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

const AccessibleReactDropzone = styled(ReactDropzone)`
  input {
    display: none;
  }
`;

const Dropzone: FC<Props> = ({
  onUpload,
  onDropAccepted,
  children,
  disabled,
  acceptImageAndPDF = false,
  multiple = false,
  activeStyle,
  ...other
}) => {
  const accept = acceptImageAndPDF ? '.pdf, application/pdf, image/*' : 'image/*';
  const dropzoneProps = {
    accept,
    multiple,
    acceptStyle: {},
    rejectStyle: {},
    disabledStyle: {},
    name: 'input-photo',
  };

  return (
    <AccessibleReactDropzone
      disabled={disabled}
      {...other}
      {...dropzoneProps}
      activeStyle={activeStyle ?? {}}
      onDrop={(acceptedFiles: DropzoneFile[], rejectedFiles: DropzoneFile[]) => {
        // remove any rejected files from memory, prevents memory leaks
        rejectedFiles.forEach((file) => file.preview && window.URL.revokeObjectURL(file.preview)); // eslint-disable-line rover/no-platform-specific-globals-or-imports
        // lets onUpload take the place of onDrop
        return onUpload
          ? onUpload(acceptedFiles, rejectedFiles)
          : other.onDrop && other.onDrop(acceptedFiles, rejectedFiles);
      }}
      onDropAccepted={onDropAccepted}
    >
      {children}
    </AccessibleReactDropzone>
  );
};

export default Dropzone;
